import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  TextField,
  Tooltip,
  LinearProgress,
  Grid,
  Button,
  Drawer,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import { ProjetServiceSuivi } from '../../../types/suivi';
import { suiviAPI } from '../../../services/api';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import AlertDialog from '../../../components/AlertDialog';
import TruncatedTextWithTooltip from '../../../components/TruncatedTextWithTooltip';
import useAlert from '../../../hooks/useAlert';
import { extractErrorMessage } from '../../../utils/errorHandler';
import ColumnFilter from '../../../components/ColumnFilter';
import { useColumnFilters } from '../../../hooks/useColumnFilters';
import { ComptoirTravauxSection } from '../components/ComptoirTravauxTable';

const noWrapCellStyle = {
  whiteSpace: 'nowrap' as const,
};

const PRODUCTION_COLOR = '#FF5722';

// Suivi tracking is fetched server-side (search + type + status), but column
// filters here are layered client-side over the in-memory items list.
const SUIVI_FILTER_SCHEMA = {
  code:           { type: 'text',      param: 'code' },
  type:           { type: 'select',    param: 'type' },
  nom:            { type: 'text',      param: 'nom' },
  client:         { type: 'text',      param: 'client' },
  responsable:    { type: 'text',      param: 'responsable' },
  date_ep:        { type: 'dateRange', paramFrom: 'date_ep_from',        paramTo: 'date_ep_to' },
  date_livraison: { type: 'dateRange', paramFrom: 'date_livraison_from', paramTo: 'date_livraison_to' },
  status:         { type: 'select',    param: 'status' },
} as const;

const DRAWER_WIDTH = 450;

const SuiviProjetService: React.FC = () => {
  const [items, setItems] = useState<ProjetServiceSuivi[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ProjetServiceSuivi | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();

  const { filters, update, reset, hasActive } = useColumnFilters(SUIVI_FILTER_SCHEMA);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchData = useCallback(async (updateSelected?: { id: number; type: string }) => {
    try {
      setLoading(true);
      // type/status are now in the column filters layer (applied client-side
      // for tighter UX). Fetch the full active set and filter in JS.
      const response = await suiviAPI.getTracking(
        undefined,
        undefined,
        debouncedSearch || undefined,
        0,
        500,
      );
      setItems(response.items);

      // Update selectedItem if provided
      if (updateSelected) {
        const updatedItem = response.items.find(
          item => item.id === updateSelected.id && item.type === updateSelected.type
        );
        if (updatedItem) {
          setSelectedItem(updatedItem);
        }
      }
    } catch (error) {
      showError(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, showError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleStep = async (stepId: number, projetId?: number, serviceId?: number) => {
    try {
      await suiviAPI.toggleProgress(stepId, projetId, serviceId);
      // Refresh data to get updated progress and update selected item
      if (selectedItem) {
        await fetchData({ id: selectedItem.id, type: selectedItem.type });
      } else {
        await fetchData();
      }
    } catch (error) {
      showError(extractErrorMessage(error));
    }
  };

  const handleInitDefaultPhases = async () => {
    showConfirm(
      'Voulez-vous initialiser les phases par defaut ? Cela creera les phases et etapes de production standard.',
      async () => {
        try {
          setLoading(true);
          await suiviAPI.initDefaultPhases();
          showSuccess('Phases initialisees avec succes');
          await fetchData();
        } catch (error) {
          showError(extractErrorMessage(error));
        } finally {
          setLoading(false);
        }
      },
      {
        title: 'Initialiser les phases',
        confirmText: 'Initialiser',
        cancelText: 'Annuler',
      }
    );
  };

  const handleRowClick = (item: ProjetServiceSuivi) => {
    setSelectedItem(item);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
  };

  const handleCheckboxChange = async (stepId: number) => {
    if (!selectedItem) return;
    const projetId = selectedItem.type === 'projet' ? selectedItem.id : undefined;
    const serviceId = selectedItem.type === 'service' ? selectedItem.id : undefined;
    await handleToggleStep(stepId, projetId, serviceId);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  const formatDateWithTime = (dateStr?: string, startTime?: string, endTime?: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr).toLocaleDateString('fr-FR');
    if (startTime && endTime) {
      return `${date} (${startTime} - ${endTime})`;
    } else if (startTime) {
      return `${date} (${startTime})`;
    }
    return date;
  };

  const getStatusChip = (status: string, joursRetard: number) => {
    switch (status) {
      case 'retard':
        return (
          <Chip
            label={`Retard (${joursRetard}j)`}
            size="small"
            sx={{ bgcolor: '#f44336', color: 'white' }}
          />
        );
      case 'satisfaisant':
        return (
          <Chip
            label="Satisfaisant"
            size="small"
            sx={{ bgcolor: '#4CAF50', color: 'white' }}
          />
        );
      case 'en_cours':
      default:
        return (
          <Chip
            label="En cours"
            size="small"
            sx={{ bgcolor: '#2196F3', color: 'white' }}
          />
        );
    }
  };

  const getTypeChip = (type: string) => {
    return type === 'projet' ? (
      <Chip label="Projet" size="small" sx={{ bgcolor: '#FF5722', color: 'white' }} />
    ) : (
      <Chip label="Service" size="small" sx={{ bgcolor: '#2196F3', color: 'white' }} />
    );
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 100) return '#4CAF50';
    if (percentage >= 50) return '#FF9800';
    return '#2196F3';
  };

  // Apply client-side column filters on the loaded items.
  const ilike = (s: any, q: string) =>
    s ? String(s).toLowerCase().includes(q.toLowerCase()) : false;
  const filteredItems = items.filter((item) => {
    if (filters.code && !ilike(item.code, filters.code)) return false;
    if (filters.type && item.type !== filters.type) return false;
    if (filters.nom && !ilike(item.nom, filters.nom)) return false;
    if (filters.client && !ilike(item.client_name, filters.client)) return false;
    if (filters.responsable && !ilike(item.responsable_name, filters.responsable)) return false;
    if (filters.date_ep.from && item.date_ep && item.date_ep.substring(0, 10) < filters.date_ep.from) return false;
    if (filters.date_ep.to && item.date_ep && item.date_ep.substring(0, 10) > filters.date_ep.to) return false;
    const livraison = item.type === 'service' ? item.date_ep : item.date_livraison;
    if (filters.date_livraison.from && livraison && livraison.substring(0, 10) < filters.date_livraison.from) return false;
    if (filters.date_livraison.to && livraison && livraison.substring(0, 10) > filters.date_livraison.to) return false;
    if (filters.status && item.status !== filters.status) return false;
    return true;
  });

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Suivi de Projet / Service</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={handleInitDefaultPhases}
            sx={{ borderColor: '#FF5722', color: '#FF5722', '&:hover': { borderColor: '#E64A19', bgcolor: '#FBE9E7' } }}
          >
            Initialiser Phases
          </Button>
          <IconButton onClick={() => fetchData()} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Rechercher par code ou nom..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            {hasActive() && (
              <Button
                size="small"
                startIcon={<FilterListOffIcon />}
                onClick={reset}
                sx={{ color: 'text.secondary' }}
              >
                Effacer les filtres
              </Button>
            )}
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              {filteredItems.length} resultat(s)
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Box sx={{ ...getModuleTableTheme('production'), overflow: 'hidden' }}>
        <TableContainer component={Paper} sx={{ overflowX: 'auto', maxWidth: '100%' }}>
          {loading && <LinearProgress sx={{ bgcolor: '#FFCCBC', '& .MuiLinearProgress-bar': { bgcolor: '#FF5722' } }} />}
          <Table sx={{ minWidth: 1100, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 110, ...noWrapCellStyle }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Code
                    <ColumnFilter type="text" value={filters.code} onChange={(v) => update('code', v)} label="code" color={PRODUCTION_COLOR} />
                  </Box>
                </TableCell>
                <TableCell sx={{ width: 80, ...noWrapCellStyle }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Type
                    <ColumnFilter
                      type="select"
                      value={filters.type}
                      onChange={(v) => update('type', v)}
                      label="type"
                      color={PRODUCTION_COLOR}
                      options={[
                        { value: 'projet', label: 'Projet' },
                        { value: 'service', label: 'Service' },
                      ]}
                    />
                  </Box>
                </TableCell>
                <TableCell sx={{ width: 150, ...noWrapCellStyle }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Nom
                    <ColumnFilter type="text" value={filters.nom} onChange={(v) => update('nom', v)} label="nom" color={PRODUCTION_COLOR} />
                  </Box>
                </TableCell>
                <TableCell sx={{ width: 130, ...noWrapCellStyle }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Client
                    <ColumnFilter type="text" value={filters.client} onChange={(v) => update('client', v)} label="client" color={PRODUCTION_COLOR} />
                  </Box>
                </TableCell>
                <TableCell sx={{ width: 120, ...noWrapCellStyle }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Responsable
                    <ColumnFilter type="text" value={filters.responsable} onChange={(v) => update('responsable', v)} label="responsable" color={PRODUCTION_COLOR} />
                  </Box>
                </TableCell>
                <TableCell sx={{ width: 180, ...noWrapCellStyle }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Date EP
                    <ColumnFilter type="dateRange" value={filters.date_ep} onChange={(v) => update('date_ep', v)} label="date EP" color={PRODUCTION_COLOR} />
                  </Box>
                </TableCell>
                <TableCell sx={{ width: 110, ...noWrapCellStyle }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Date Livraison
                    <ColumnFilter type="dateRange" value={filters.date_livraison} onChange={(v) => update('date_livraison', v)} label="date livraison" color={PRODUCTION_COLOR} />
                  </Box>
                </TableCell>
                <TableCell sx={{ width: 150, ...noWrapCellStyle }}>Progression</TableCell>
                <TableCell sx={{ width: 100, ...noWrapCellStyle }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Statut
                    <ColumnFilter
                      type="select"
                      value={filters.status}
                      onChange={(v) => update('status', v)}
                      label="statut"
                      color={PRODUCTION_COLOR}
                      options={[
                        { value: 'en_cours', label: 'En cours' },
                        { value: 'retard', label: 'Retard' },
                        { value: 'satisfaisant', label: 'Satisfaisant' },
                      ]}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.map((item) => (
                <TableRow
                  key={`${item.type}-${item.id}`}
                  hover
                  sx={{
                    cursor: 'pointer',
                    bgcolor: selectedItem?.id === item.id && selectedItem?.type === item.type ? '#FBE9E7' : 'inherit',
                    '&:hover': {
                      bgcolor: '#FBE9E7',
                    },
                  }}
                  onClick={() => handleRowClick(item)}
                >
                  <TableCell sx={noWrapCellStyle}>
                    <Typography variant="body2" fontWeight="medium">
                      {item.code}
                    </Typography>
                  </TableCell>
                  <TableCell sx={noWrapCellStyle}>{getTypeChip(item.type)}</TableCell>
                  <TableCell sx={noWrapCellStyle}>
                    <TruncatedTextWithTooltip text={item.nom} maxWidth={180} />
                  </TableCell>
                  <TableCell sx={noWrapCellStyle}>
                    <TruncatedTextWithTooltip text={item.client_name} maxWidth={150} />
                  </TableCell>
                  <TableCell sx={noWrapCellStyle}>
                    <TruncatedTextWithTooltip text={item.responsable_name} maxWidth={150} />
                  </TableCell>
                  <TableCell sx={noWrapCellStyle}>{formatDateWithTime(item.date_ep, item.start_time, item.end_time)}</TableCell>
                  <TableCell sx={noWrapCellStyle}>{formatDate(item.type === 'service' ? item.date_ep : item.date_livraison)}</TableCell>
                  <TableCell sx={{ minWidth: 150, ...noWrapCellStyle }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={item.status === 'satisfaisant' ? 100 : item.progress_percentage}
                        sx={{
                          flex: 1,
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'divider',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: item.status === 'satisfaisant' ? '#4CAF50' : getProgressColor(item.progress_percentage),
                            borderRadius: 4,
                          },
                        }}
                      />
                      <Typography variant="body2" fontWeight="medium" sx={{ minWidth: 40 }}>
                        {item.status === 'satisfaisant' ? '100' : item.progress_percentage.toFixed(0)}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={noWrapCellStyle}>{getStatusChip(item.status, item.jours_retard)}</TableCell>
                </TableRow>
              ))}
              {filteredItems.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      Aucun projet ou service trouve
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Side Drawer for Phases/Steps */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        {selectedItem && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box
              sx={{
                p: 2,
                bgcolor: '#FF5722',
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {selectedItem.code}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {selectedItem.nom}
                </Typography>
                {selectedItem.client_name && (
                  <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                    Client: {selectedItem.client_name}
                  </Typography>
                )}
                {selectedItem.responsable_name && (
                  <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                    {selectedItem.responsable_name.includes(' · ') ? 'Responsables' : 'Responsable'}: {selectedItem.responsable_name}
                  </Typography>
                )}
              </Box>
              <IconButton onClick={handleCloseDrawer} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Progress Summary */}
            <Box sx={{ p: 2, bgcolor: 'background.default' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={selectedItem.status === 'satisfaisant' ? 100 : selectedItem.progress_percentage}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      bgcolor: 'divider',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: selectedItem.status === 'satisfaisant' ? '#4CAF50' : getProgressColor(selectedItem.progress_percentage),
                        borderRadius: 5,
                      },
                    }}
                  />
                </Box>
                <Typography variant="h6" fontWeight="bold" sx={{ color: selectedItem.status === 'satisfaisant' ? '#4CAF50' : getProgressColor(selectedItem.progress_percentage) }}>
                  {selectedItem.status === 'satisfaisant' ? '100' : selectedItem.progress_percentage.toFixed(0)}%
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                {getTypeChip(selectedItem.type)}
                {getStatusChip(selectedItem.status, selectedItem.jours_retard)}
              </Box>
            </Box>

            {/* Comptoir job: what the client asked for, with its lines 2.1, 2.2… (nothing for other rows) */}
            <ComptoirTravauxSection type={selectedItem.type} id={selectedItem.id} />

            <Divider />

            {/* Phases and Steps */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {selectedItem.phases.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 4 }}>
                  Aucune phase configurée. Cliquez sur "Initialiser Phases" pour créer les phases par défaut.
                </Typography>
              ) : (
                selectedItem.phases.map((phase, phaseIndex) => {
                  const completedSteps = phase.steps.filter(s => s.is_completed).length;
                  const totalSteps = phase.steps.length;
                  const phaseProgress = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;

                  return (
                    <Box key={phase.id} sx={{ mb: 3 }}>
                      {/* Phase Header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            bgcolor: phaseProgress === 100 ? '#4CAF50' : '#FF5722',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.8rem',
                            fontWeight: 'bold',
                          }}
                        >
                          {phaseIndex + 1}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {phase.name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                            {phase.atelier_name && (
                              <Chip
                                label={phase.atelier_name}
                                size="small"
                                sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'divider' }}
                              />
                            )}
                            {phase.responsable_name && (
                              <Chip
                                label={phase.responsable_name}
                                size="small"
                                sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#FF5722', color: 'white' }}
                              />
                            )}
                          </Box>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {completedSteps}/{totalSteps}
                        </Typography>
                      </Box>

                      {/* Steps */}
                      <Box sx={{ ml: 4.5, borderLeft: 2, borderColor: 'divider', pl: 2 }}>
                        {phase.steps.length === 0 ? (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Aucune étape
                          </Typography>
                        ) : (
                          phase.steps.map((step) => {
                            const isAtelierPhase = !!phase.atelier_id;
                            const canToggle = !isAtelierPhase;

                            return (
                            <Box
                              key={step.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                py: 0.75,
                                px: 1,
                                borderRadius: 1,
                                bgcolor: step.is_completed ? '#e8f5e9' : 'transparent',
                                cursor: canToggle ? 'pointer' : 'default',
                                '&:hover': canToggle ? {
                                  bgcolor: step.is_completed ? '#c8e6c9' : '#fff3e0',
                                } : {},
                              }}
                              onClick={() => canToggle && handleCheckboxChange(step.id)}
                            >
                              {step.is_completed ? (
                                <CheckCircleIcon sx={{ fontSize: 20, color: '#4CAF50' }} />
                              ) : (
                                <UncheckedIcon sx={{ fontSize: 20, color: '#FF5722' }} />
                              )}
                              <Typography
                                variant="body2"
                                sx={{
                                  flex: 1,
                                  textDecoration: step.is_completed ? 'line-through' : 'none',
                                  color: step.is_completed ? 'text.secondary' : 'text.primary',
                                }}
                              >
                                {step.name}
                              </Typography>
                              {step.is_completed && step.completed_at && (
                                <Tooltip title={`Par: ${step.completed_by_name || 'N/A'}`}>
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(step.completed_at).toLocaleDateString('fr-FR')}
                                  </Typography>
                                </Tooltip>
                              )}
                            </Box>
                            );
                          })
                        )}
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>
        )}
      </Drawer>

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default SuiviProjetService;
